// Implements: F-MCK-021, F-UIX-010, F-UIX-013
/**
 * "Transferir la propiedad" (mockup v3 :2817-2830), from the card menu and the editor.
 *
 * The candidates are the organization's people (`shared/api/people`) without the current owner.
 * The transfer is sent once (a ref guard, `retry: false` in the client) and nothing is updated
 * before the server answers: a refusal (`422` recipient outside the scope, `409` personal, `403`)
 * keeps the dialog open with the server's sentence and the owner unchanged on screen. A personal
 * mockup is not transferable (the server says so with `409`), so the dialog explains it and offers
 * no submit. Mounted keyed by the mockup id (checklist 14).
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";

import { asApiError } from "../../../shared/api/asApiError";
import { fetchOrganizationPeople } from "../../../shared/api/people";
import { useViewerId } from "../../../shared/api/viewer";
import { MOCKUPS_KEY, mockupAssignmentsKey, mockupDetailKey, organizationPeopleKey } from "../../../shared/api/queryKeys";
import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { Dialog } from "../../../shared/ui/organisms/Dialog";
import { transferOwnership } from "../api/mockupsApi";
import { TransferOwnershipForm } from "../components/TransferOwnershipForm";
import type { TransferCandidate } from "../components/TransferOwnershipForm";

/** The transfer route writes these refusals for people too (personal scope, recipient outside). */
const TRANSFER_REFUSAL_CODES = ["CONFLICT"] as const;

export interface TransferOwnershipDialogProps {
  readonly mockupId: string;
  readonly mockupName: string;
  readonly organizationId: string | null;
  readonly ownerId: string;
  readonly personal: boolean;
  readonly onClose: () => void;
  /** Called with the new owner's id after the server accepted the transfer. */
  readonly onTransferred?: ((ownerId: string) => void) | undefined;
}

export function TransferOwnershipDialog({
  mockupId,
  mockupName,
  organizationId,
  ownerId,
  personal,
  onClose,
  onTransferred,
}: TransferOwnershipDialogProps): ReactElement {
  const { t } = useTranslation();
  const { push } = useToast();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState<string | undefined>(undefined);
  const sending = useRef(false);
  const transferable = !personal && organizationId !== null;
  const viewerId = useViewerId();

  const people = useQuery({
    queryKey: organizationPeopleKey(organizationId ?? ""),
    queryFn: ({ signal }) => fetchOrganizationPeople(organizationId ?? "", signal),
    enabled: transferable,
  });
  const candidates = useMemo<readonly TransferCandidate[]>(
    () =>
      (people.data ?? [])
        // An absent status is a team lead's view (P3-2): the server already kept the active seats.
        .filter((member) => member.user.id !== ownerId && (member.status ?? "active") === "active")
        .map((member) => ({ id: member.user.id, name: member.user.full_name, email: member.user.email })),
    [people.data, ownerId],
  );

  const submit = async (): Promise<void> => {
    if (sending.current || selected === null) return;
    sending.current = true;
    setPending(true);
    setRefusal(undefined);
    try {
      const detail = await transferOwnership(mockupId, selected);
      const person = candidates.find((candidate) => candidate.id === selected)?.name ?? selected;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: MOCKUPS_KEY }),
        queryClient.invalidateQueries({ queryKey: mockupDetailKey(mockupId) }),
        queryClient.invalidateQueries({ queryKey: mockupAssignmentsKey(mockupId) }),
      ]);
      push({ tone: "success", message: t("mockups.transfer.done", { name: mockupName, person }) });
      onTransferred?.(detail.owner_id);
      onClose();
    } catch (cause) {
      setRefusal(userMessage(asApiError(cause), t("mockups.transfer.failed"), TRANSFER_REFUSAL_CODES));
    } finally {
      sending.current = false;
      setPending(false);
    }
  };

  let body: ReactElement;
  if (!transferable) body = <p>{t("mockups.transfer.personal")}</p>;
  else if (people.isPending) body = <p>{t("mockups.transfer.loading")}</p>;
  else if (people.isError) body = <p role="alert">{t("mockups.transfer.peopleFailed")}</p>;
  else
    body = (
      <TransferOwnershipForm
        mockupName={mockupName}
        notice={
          viewerId === ownerId
            ? t("mockups.transfer.notice")
            : t("mockups.transfer.noticeOther", {
                name: people.data.find((member) => member.user.id === ownerId)?.user.full_name ?? t("sharing.people.owner"),
              })
        }
        candidates={candidates}
        selected={selected}
        onSelect={setSelected}
        disabled={pending}
        error={refusal}
      />
    );

  return (
    <Dialog
      open
      title={t("mockups.transfer.title")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.action.cancel")}
          </Button>
          {transferable ? (
            <Button
              variant="primary"
              loading={pending}
              disabled={selected === null}
              onClick={() => {
                void submit();
              }}
            >
              {t("mockups.transfer.submit")}
            </Button>
          ) : null}
        </>
      }
    >
      {body}
    </Dialog>
  );
}
